import type { FetchResult } from '@apollo/client';
import type { ApolloError } from '@apollo/client/errors';
import type { ValidationError } from 'generated/gql';
import type { ValidationErrorCode } from 'forms/helpers/setFormErrors';
import type { UseSetFormValidationErrorsProps } from 'forms/mutation/useSetFormValidationErrors';
import { useSetFormValidationErrors } from 'forms/mutation/useSetFormValidationErrors';
import { useCallback } from 'react';

const stripAllowedErrors = (errors: unknown[], allowedErrors: string[]) =>
  errors.filter(
    (error): error is ValidationError =>
      !(
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        allowedErrors.includes(error.code as never)
      ),
  );

type UseHandleMutationResponseProps<ReturnData = any> = {
  onSuccess?: (data: ReturnData) => void;
  graphQlErrorMapper?: (errors: ApolloError['graphQLErrors']) => ValidationError[];
  onApolloError?: (error: ApolloError) => void;
  allowedErrors?: ValidationErrorCode[];
} & UseSetFormValidationErrorsProps;

export const useHandleMutationResponse = <ReturnData = any>({
  onSuccess,
  allowedErrors = [],
  onApolloError,
  graphQlErrorMapper = (graphQLErrors) =>
    graphQLErrors.map(({ extensions }) => extensions?.validationError as ValidationError),
  ...props
}: UseHandleMutationResponseProps<ReturnData>) => {
  const setFormValidationErrors = useSetFormValidationErrors(props);
  const handleResponse = useCallback(
    (result: Record<string, any> | null | undefined | FetchResult) => {
      const { data, errors } = result as FetchResult;

      if (errors) {
        setFormValidationErrors(graphQlErrorMapper(errors as ApolloError['graphQLErrors']));
        return;
      }

      // Process data from FetchResult
      if (data) {
        return processResponseData(data);
      }

      // No data case
      setFormValidationErrors([]);
    },
    [allowedErrors, graphQlErrorMapper, onSuccess, setFormValidationErrors],
  );

  // Helper function to process response data
  const processResponseData = useCallback(
    (data: Record<string, any>) => {
      // Check for validation errors in the response
      const errorInResponse = Object.values(data).find(
        (v) => typeof v === 'object' && v !== null && 'validationErrors' in v,
      ) as { validationErrors: ValidationError[] } | undefined;

      if (errorInResponse?.validationErrors.length) {
        const forbiddenErrors = allowedErrors.length
          ? stripAllowedErrors(errorInResponse.validationErrors, allowedErrors)
          : errorInResponse.validationErrors;
        if (forbiddenErrors.length) {
          setFormValidationErrors(forbiddenErrors);
          return;
        }
      }

      if (onSuccess) onSuccess(data as ReturnData);
      return data;
    },
    [allowedErrors, onSuccess, setFormValidationErrors],
  );
  const handleError = useCallback(
    (error: Error) => {
      console.error(error);
      if ('graphQLErrors' in error) {
        setFormValidationErrors(
          graphQlErrorMapper(error.graphQLErrors as ApolloError['graphQLErrors']),
        );
      } else {
        setFormValidationErrors([]);
      }
      if (error.name === 'ApolloError' && onApolloError) onApolloError(error as ApolloError);
    },
    [graphQlErrorMapper, onApolloError, setFormValidationErrors],
  );

  return [handleResponse, handleError] as const;
};
